/**
 * Role display name mapping
 * Maps Azure AD role names to user-friendly display names
 */

export enum UserRoleDisplay {
    Reader = 'Reader',
    Writer = 'Writer',
    ChemGuardWriter = 'ChemGuardWriter',
}

/**
 * Maps Azure AD role names to display names
 */
const roleDisplayMap: Record<string, UserRoleDisplay> = {
    OdfjellUser: UserRoleDisplay.Reader,
    CommodityBookEditor: UserRoleDisplay.Writer,
    CommodityBookChemGuardEditor: UserRoleDisplay.ChemGuardWriter,
};

/**
 * Gets the display name for a role
 * @param roleName The Azure AD role name
 * @returns The user-friendly display name, or the original role name if not mapped
 */
export const getRoleDisplayName = (roleName: string): string => {
    return roleDisplayMap[roleName] || roleName;
};

/**
 * Maps an array of Azure AD role names to their display names
 * @param roles Array of Azure AD role names
 * @returns Array of user-friendly display names
 */
export const mapRolesToDisplayNames = (roles: string[]): string[] => {
    return roles.map(getRoleDisplayName);
};

/**
 * Checks if a role is mapped to a display name
 * @param roleName The Azure AD role name
 * @returns True if the role has a display name mapping
 */
export const isMappedRole = (roleName: string): boolean => {
    return roleName in roleDisplayMap;
};

/**
 * Gets unique display names for roles (removes duplicates after mapping)
 * Only includes roles that have explicit mappings (filters out unmapped roles like CommodityBookAuditor)
 * @param roles Array of Azure AD role names
 * @returns Array of unique user-friendly display names
 */
export const getUniqueRoleDisplayNames = (roles: string[]): string[] => {
    const mappedRoles = roles.filter(isMappedRole);
    const displayNames = mapRolesToDisplayNames(mappedRoles);
    return [...new Set(displayNames)];
};
